import { Switch } from '@/components/ui/switch'
import { Button } from '@/components/ui/button'
import { Label } from '@/components/ui/label'
import { Plus, X } from 'lucide-react'

export type TimeSlot = { start: string; end: string }
export type DayAvailability = { enabled: boolean; slots: TimeSlot[] }
export type WeekAvailability = Record<string, DayAvailability>

export const DAYS = [
  { key: 'monday',    label: 'Segunda-feira' },
  { key: 'tuesday',   label: 'Terça-feira' },
  { key: 'wednesday', label: 'Quarta-feira' },
  { key: 'thursday',  label: 'Quinta-feira' },
  { key: 'friday',    label: 'Sexta-feira' },
  { key: 'saturday',  label: 'Sábado' },
  { key: 'sunday',    label: 'Domingo' },
]

export function buildDefaultWeek(): WeekAvailability {
  return Object.fromEntries(DAYS.map(d => [d.key, { enabled: false, slots: [] }]))
}

interface Props {
  week: WeekAvailability
  onChange: (week: WeekAvailability) => void
  disabled?: boolean
}

export function AvailabilityEditor({ week, onChange, disabled = false }: Props) {
  const toggleDay = (day: string) => {
    const current = week[day]
    const enabling = !current.enabled
    onChange({
      ...week,
      [day]: {
        enabled: enabling,
        slots: enabling && current.slots.length === 0
          ? [{ start: '08:00', end: '17:00' }]
          : current.slots,
      },
    })
  }

  const addSlot = (day: string) => {
    onChange({
      ...week,
      [day]: {
        ...week[day],
        slots: [...week[day].slots, { start: '09:00', end: '10:00' }],
      },
    })
  }

  const removeSlot = (day: string, idx: number) => {
    const slots = week[day].slots.filter((_, i) => i !== idx)
    onChange({
      ...week,
      [day]: { enabled: slots.length > 0, slots },
    })
  }

  const updateSlot = (day: string, idx: number, field: 'start' | 'end', value: string) => {
    onChange({
      ...week,
      [day]: {
        ...week[day],
        slots: week[day].slots.map((s, i) => i === idx ? { ...s, [field]: value } : s),
      },
    })
  }

  return (
    <div className="space-y-3">
      {DAYS.map(({ key, label }) => {
        const day = week[key]
        return (
          <div
            key={key}
            className={`border rounded-lg p-3 space-y-2 transition-colors ${
              day.enabled ? 'border-primary/30 bg-primary/5' : 'border-border'
            }`}
          >
            <div className="flex items-center justify-between">
              <Label
                className="font-medium cursor-pointer select-none"
                htmlFor={`avail-switch-${key}`}
              >
                {label}
              </Label>
              <Switch
                id={`avail-switch-${key}`}
                checked={day.enabled}
                onCheckedChange={() => !disabled && toggleDay(key)}
                disabled={disabled}
              />
            </div>

            {day.enabled && (
              <div className="space-y-2 pt-1">
                {day.slots.map((slot, idx) => (
                  <div key={idx} className="flex items-center gap-2 flex-wrap">
                    <input
                      type="time"
                      value={slot.start}
                      onChange={e => updateSlot(key, idx, 'start', e.target.value)}
                      disabled={disabled}
                      className="border border-input rounded-md px-2 py-1 text-sm w-28 bg-background focus:outline-none focus:ring-1 focus:ring-ring disabled:opacity-50"
                    />
                    <span className="text-muted-foreground text-xs">às</span>
                    <input
                      type="time"
                      value={slot.end}
                      onChange={e => updateSlot(key, idx, 'end', e.target.value)}
                      disabled={disabled}
                      className="border border-input rounded-md px-2 py-1 text-sm w-28 bg-background focus:outline-none focus:ring-1 focus:ring-ring disabled:opacity-50"
                    />
                    {!disabled && (
                      <Button
                        variant="ghost"
                        size="icon"
                        className="h-7 w-7 text-muted-foreground hover:text-destructive"
                        onClick={() => removeSlot(key, idx)}
                        type="button"
                      >
                        <X className="h-3.5 w-3.5" />
                      </Button>
                    )}
                  </div>
                ))}

                {!disabled && (
                  <Button
                    variant="outline"
                    size="sm"
                    className="h-7 text-xs gap-1 mt-1"
                    onClick={() => addSlot(key)}
                    type="button"
                  >
                    <Plus className="h-3 w-3" />
                    Adicionar horário
                  </Button>
                )}
              </div>
            )}
          </div>
        )
      })}
    </div>
  )
}
